/* eslint-disable @angular-eslint/no-input-rename */
/* eslint-disable @angular-eslint/no-output-rename */
import * as _ from 'lodash-es';
import {
  Directive,
  EventEmitter,
  Input,
  Output,
  OnInit,
  effect,
  signal,
  WritableSignal,
  DestroyRef,
  inject,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { GroupResult, State } from '@progress/kendo-data-query';
import { FilterUtils } from './../../Data-Query/filter-utils';
import { PathSegmentDetached } from './../Group-Result/path-segment-detached';
import { GroupCollapseExpandUtils } from './Utils/group-collapse-expand-utils';
import { GroupingExViewModel } from './grouping-ex.view-model';
import { GroupingExSignalViewModel } from './grouping-ex-signal.view-model';
import {
  GroupingExDataStateSupport,
  IGroupingExDataState,
} from './grouping-ex.data-state.support';
import { GroupingExLoadResult } from './grouping-ex.load';
import { GroupingExSignalLoad } from './grouping-ex-signal.load';
import { GroupingExLoadSpecificGroups } from './grouping-ex.load-specific-groups';
import { LogService } from '@ups/xplat/core';

/**
 * Signal-based GroupingEx directive for modern Angular
 * Provides better reactivity and prepares for zoneless mode
 */
@Directive({
  selector: '[grid-grouping-ex-signal]',
  standalone: false,
})
export class GroupingExSignalDirective implements OnInit {
  private destroyRef = inject(DestroyRef);
  private log = inject(LogService);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);

  /**
   * Safely trigger change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
   * Defers detectChanges to the next tick and ensures it runs inside Angular's zone
   */
  private safeDetectChanges(): void {
    setTimeout(() => {
      this.ngZone.run(() => {
        this.cdr.detectChanges();
      });
    }, 0);
  }

  @Output('ggx-dataLoadedEvent')
  public dataLoadedEvent: EventEmitter<GroupingExLoadResult> =
    new EventEmitter<GroupingExLoadResult>();

  @Input('ggx-viewModel')
  public set viewModel(vm: GroupingExSignalViewModel) {
    this.viewModelSignal.set(vm);
  }
  public get viewModel(): GroupingExSignalViewModel {
    return this.viewModelSignal()!;
  }

  private readonly viewModelSignal = signal<GroupingExSignalViewModel | null>(
    null
  );

  // Internal state
  private loadDataSpecialState: IGroupingExDataState | null = null;
  private currentLoadAbortController: AbortController | null = null;

  // Signal to trigger data load
  private readonly loadTrigger: WritableSignal<{
    state: State;
    newItem?: unknown;
    timestamp: number;
    notifier: Subject<GroupingExLoadResult>;
  } | null> = signal(null);

  constructor(public grid: GridComponent) {
    // Watch for group expand events
    this.grid.groupExpand
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.internalGroupExpand.bind(this));

    // Effect to handle data loading when triggered
    effect(() => {
      const trigger = this.loadTrigger();
      const vm = this.viewModelSignal();
      if (trigger && vm) {
        this.performLoad(trigger.state, trigger.notifier, trigger.newItem);
      }
    });

    // Effect to sync grid.loading with viewModel.loading
    effect(() => {
      const vm = this.viewModelSignal();
      if (vm) {
        this.grid.loading = vm.loading();
        // CRITICAL: Trigger change detection for Kendo 21.x
        this.safeDetectChanges();
      }
    });
  }

  public ngOnInit(): void {
    const vm = this.viewModelSignal();
    if (vm && vm.loadDataOnInit()) {
      const gridState = {
        skip: this.grid.skip,
        take: this.grid.pageSize,
        sort: this.grid.sort,
        filter: this.grid.filter,
        group: this.grid.group,
      } as State;

      this.loadData(gridState);
    }
  }

  /**
   * Public API to load data
   * Returns an Observable for backward compatibility with components expecting subscribe()
   */
  public loadData(
    state: State,
    newItem: unknown = null
  ): Observable<GroupingExLoadResult> {
    const notifier = new Subject<GroupingExLoadResult>();

    const vm = this.viewModelSignal();
    if (!vm) {
      console.warn(
        'GroupingExSignalDirective: loadData called before viewModel was set.'
      );
      setTimeout(() => {
        const result = new GroupingExLoadResult();
        notifier.next(result);
        notifier.complete();
      });
      return notifier.asObservable().pipe(take(1));
    }

    // Cancel any previous load
    if (this.currentLoadAbortController) {
      this.currentLoadAbortController.abort();
    }

    // Trigger new load
    this.loadTrigger.set({
      state: _.cloneDeep(state),
      newItem,
      timestamp: Date.now(),
      notifier,
    });

    // Return observable that emits when data is loaded
    return notifier.asObservable().pipe(take(1));
  }

  /**
   * Cancel current load operation
   */
  public cancel(): void {
    if (this.currentLoadAbortController) {
      this.currentLoadAbortController.abort();
      this.currentLoadAbortController = null;
    }
    this.viewModel?.loading.set(false);

    // If there's an active load trigger, notify it of abortion
    const trigger = this.loadTrigger();
    if (trigger) {
      const abortResult = new GroupingExLoadResult();
      abortResult.aborted = true;
      trigger.notifier.next(abortResult);
      trigger.notifier.complete();
    }

    // CRITICAL: Trigger change detection after cancel for Kendo 21.x
    this.safeDetectChanges();
  }

  /**
   * Get state with extra filters applied
   */
  public getModdedState(state: State): State {
    const stateCopy = _.cloneDeep(state) as State;
    FilterUtils.replaceFilter(
      stateCopy,
      FilterUtils.concatFilters(
        stateCopy.filter,
        this.viewModel.extraFilter,
        this.viewModel.extraFilterLogic
      )
    );
    return stateCopy;
  }

  /**
   * Perform the actual data load
   * Note: This method manages the loading state for the entire operation
   */
  private async performLoad(
    state: State,
    notifier: Subject<GroupingExLoadResult>,
    newItem: unknown = null
  ): Promise<void> {
    // Keep reference to this specific execution's controller
    const controller = new AbortController();
    this.currentLoadAbortController = controller;
    const signal = controller.signal;

    try {
      this.viewModel.loading.set(true);
      this.safeDetectChanges();

      // Reset skip if grouping changed
      this.refreshPageGroupingChanged(this.grid, state);

      const mergedGridState = this.getModdedState(state);

      // Get current state for restoration (if no page switch and not partially loaded)
      this.loadDataSpecialState =
        (this.grid.skip || 0) === (state.skip || 0)
          ? GroupingExDataStateSupport.fetchCurrentState(this.grid)
          : null;

      // Load level 1 data
      const level1Data = await GroupingExSignalLoad.loadData(
        this.viewModel,
        this.grid,
        mergedGridState,
        signal
      );

      // Check if aborted
      if (signal.aborted) {
        const abortResult = new GroupingExLoadResult();
        abortResult.aborted = true;
        notifier.next(abortResult);
        notifier.complete();
        return;
      }

      GroupingExSignalLoad.mergeLoadDataResultToGrid(
        this.viewModel,
        this.grid,
        mergedGridState,
        level1Data,
        newItem
      );

      // CRITICAL: Trigger change detection after merging L1 data for Kendo 21.x
      this.safeDetectChanges();

      // Handle pagination edge case
      if (level1Data.loadedItemSets && level1Data.loadedItemSets.length) {
        const sumOfItems = level1Data.loadedItemSets.reduce(
          (acc, lgs) => acc + lgs.total,
          0
        );
        if (sumOfItems < mergedGridState.skip) {
          mergedGridState.skip = 0;
          this.loadData(mergedGridState).subscribe((res) => {
            notifier.next(res);
            notifier.complete();
          });
          return;
        }
      }

      // Handle grouped data - check if we need to load all items
      if (level1Data.loadedGroupSets && level1Data.loadedGroupSets.length) {
        const sumOfItems = level1Data.loadedGroupSets.reduce(
          (lgsAcc, lgs) =>
            lgsAcc +
            (lgs.data || []).reduce((dataAcc, data) => dataAcc + data.count, 0),
          0
        );

        if (sumOfItems < this.viewModel.loadAllItemsUntilCountOf()) {
          await this.loadAllItemsForGroups(
            level1Data,
            mergedGridState,
            newItem,
            signal
          );
          if (signal.aborted) {
            const abortResult = new GroupingExLoadResult();
            abortResult.aborted = true;
            notifier.next(abortResult);
          } else {
            notifier.next(level1Data);
          }
          notifier.complete();
          return;
        }
      }

      // Handle state restoration
      if (this.loadDataSpecialState) {
        await this.restoreDataState(mergedGridState, signal);
      }

      if (signal.aborted) {
        const abortResult = new GroupingExLoadResult();
        abortResult.aborted = true;
        notifier.next(abortResult);
      } else {
        this.raiseDataLoadedEvent(level1Data);
        notifier.next(level1Data);
      }
      notifier.complete();
    } catch (error) {
      if (!signal.aborted) {
        console.error(
          'Error loading grid data in GroupingExSignalDirective:',
          error
        );
        this.grid.data = { data: [], total: 0 };
        const errorResult = new GroupingExLoadResult();
        this.raiseDataLoadedEvent(errorResult);
        notifier.next(errorResult);
        notifier.complete();
        this.safeDetectChanges();
      } else {
        const abortResult = new GroupingExLoadResult();
        abortResult.aborted = true;
        notifier.next(abortResult);
        notifier.complete();
      }
    } finally {
      // Only reset if this was the latest load started
      if (this.currentLoadAbortController === controller) {
        this.currentLoadAbortController = null;
        this.viewModel.loading.set(false);
      }
      // CRITICAL: Trigger change detection when load completes for Kendo 21.x
      this.safeDetectChanges();
    }
  }

  /**
   * Load all items for small groups
   */
  private async loadAllItemsForGroups(
    level1Data: GroupingExLoadResult,
    mergedGridState: State,
    newItem: unknown,
    signal?: AbortSignal
  ): Promise<void> {
    const itemState4Load = _.cloneDeep(mergedGridState);
    delete itemState4Load['skip'];
    delete itemState4Load['take'];

    const groupsInGrid = level1Data.processedGroupSets[0].data.map(
      (i: GroupResult) => [
        { field: i.field, value: i.value } as PathSegmentDetached,
      ]
    );

    GroupingExLoadSpecificGroups.extendFilterFor(
      this.grid,
      itemState4Load,
      groupsInGrid,
      1
    );

    const d = await GroupingExSignalLoad.loadLevelsBelow(
      this.viewModel,
      this.grid,
      itemState4Load,
      null, // Loading for the whole grid, no specific group item
      signal
    );

    if (signal?.aborted) {
      return;
    }

    const gridData = this.grid.data as GridDataResult;
    GroupingExSignalLoad.mergeLoadLevelsBelowResultToGrid(
      this.viewModel,
      this.grid,
      itemState4Load,
      null,
      d
    );

    GroupCollapseExpandUtils.expandAll(this.grid, gridData.data);

    if (this.loadDataSpecialState) {
      GroupingExDataStateSupport.refreshGridState(
        this.grid,
        this.loadDataSpecialState.selectedItemDetails,
        this.viewModel.getEqualityComparer()
      );
    }

    // CRITICAL: Trigger change detection after loading sub-levels for Kendo 21.x
    this.safeDetectChanges();
    this.raiseDataLoadedEvent(d);
  }

  /**
   * Restore previous data state (expansion/selection)
   */
  private async restoreDataState(
    mergedGridState: State,
    signal?: AbortSignal
  ): Promise<void> {
    const nestedMergedGridState = _.cloneDeep(mergedGridState);
    delete nestedMergedGridState['skip'];
    delete nestedMergedGridState['take'];

    const stateData = await new Promise<GroupingExLoadResult>(
      (resolve, reject) => {
        const sub = GroupingExDataStateSupport.loadStateData(
          this.viewModel as unknown as GroupingExViewModel,
          this.grid,
          nestedMergedGridState,
          this.loadDataSpecialState!
        );

        const abortHandler = () => {
          sub.unsubscribe();
          resolve(new GroupingExLoadResult());
        };

        if (signal) {
          if (signal.aborted) {
            abortHandler();
            return;
          }
          signal.addEventListener('abort', abortHandler, { once: true });
        }

        sub.subscribe({
          next: (data) => {
            if (signal) {
              signal.removeEventListener('abort', abortHandler);
            }
            resolve(data);
          },
          error: (err) => {
            if (signal) {
              signal.removeEventListener('abort', abortHandler);
            }
            reject(err);
          },
        });
      }
    );

    if (signal?.aborted) {
      return;
    }

    GroupingExDataStateSupport.mergeStateDataResultToGrid(
      this.viewModel as unknown as GroupingExViewModel,
      this.grid,
      nestedMergedGridState,
      stateData,
      this.loadDataSpecialState!.selectedItemDetails,
      this.viewModel.getEqualityComparer()
    );

    // CRITICAL: Trigger change detection after state restoration for Kendo 21.x
    this.safeDetectChanges();
    this.raiseDataLoadedEvent(stateData);
  }

  /**
   * Handle group expand event
   */
  private internalGroupExpand(event: {
    group: GroupResult;
    groupIndex: string;
  }): void {
    const groupDataItem = event.group;

    // If there are items, then we don't need to load
    const hasItemsBelow = groupDataItem.items && groupDataItem.items.length;
    if (hasItemsBelow) {
      return;
    }

    setTimeout(async () => {
      // Create dedicated AbortController for group expand operations
      const groupExpandController = new AbortController();
      const signal = groupExpandController.signal;

      this.viewModel.loading.set(true);

      try {
        const gridState = {
          skip: this.grid.skip,
          take: this.grid.pageSize,
          sort: this.grid.sort,
          filter: this.grid.filter,
          group: this.grid.group,
        } as State;

        const mergedGridState = this.getModdedState(gridState);

        const d = await GroupingExSignalLoad.loadNextLevel(
          this.viewModel,
          this.grid,
          mergedGridState,
          groupDataItem,
          signal
        );

        if (signal?.aborted) return;

        this.raiseDataLoadedEvent(d);

        // OData open types field handling
        if (
          groupDataItem &&
          groupDataItem.value &&
          this.viewModel.oDataOpenTypesFieldName &&
          d.processedItemSets &&
          d.processedItemSets.length
        ) {
          if (
            d.processedItemSets[0].data &&
            d.processedItemSets[0].data.length &&
            !d.processedItemSets[0].data[0].value
          ) {
            d.processedItemSets[0].data[0].value = groupDataItem.value;
          }
        }

        GroupingExSignalLoad.mergeLoadNextLevelResultToGrid(
          this.viewModel,
          this.grid,
          mergedGridState,
          groupDataItem,
          d
        );

        // CRITICAL: Trigger change detection after group expand for Kendo 21.x
        this.safeDetectChanges();
      } catch (error) {
        if (!signal?.aborted) {
          console.error('Error loading group data:', error);
        }
      } finally {
        this.viewModel.loading.set(false);
        // CRITICAL: Trigger change detection when loading completes for Kendo 21.x
        this.safeDetectChanges();
      }
    }, 10); // #8022 - to be able to execute groupExpand before internalGroupExpand
  }

  /**
   * Reset skip if grouping changed
   */
  private refreshPageGroupingChanged(grid: GridComponent, state: State): void {
    if (!_.isEqual(grid.group, state.group)) {
      state.skip = 0;
      grid.skip = 0;
    }
  }

  /**
   * Raise data loaded event
   */
  public raiseDataLoadedEvent(d: GroupingExLoadResult): void {
    this.dataLoadedEvent.emit(d);
  }
}
